// @intent: Iframe-side protocol adapter between parent postMessage commands and RuntimeCanvas state
// @flow: validates parent messages -> reducer -> posts ready/render/error/selection acknowledgements
// @stale: when R7 ephemeral runtime adds server-issued session credentials

import React from 'react';
import {
  ParentToRuntimeMessageSchema,
  createRuntimeMessage,
  type JsonValue,
  type ParentToRuntimeMessage,
  type RuntimeInputPayload,
  type RuntimeToParentMessage,
} from '@mintyads/shared-types';
import type { JsonObject as PlayableJsonObject } from '@mintyads/playable-api';
import { RuntimeCanvas, type RuntimeSnapshotFacts } from './RuntimeCanvas';
import { initialRuntimeState, runtimeReducer } from './runtime-state';
import { FAIL_CLOSED_MATCH3_SNAPSHOT, type RuntimeMatch3Controls } from './pixi/match3DriveCapture';
import { createRuntimeProbeTraceBuffer, type RuntimeProbeTraceBuffer } from './probe/runtimeProbeTrace';
import type { RuntimeProbeActionRecord, RuntimeProbeControls } from './probe/runtimeProbeTypes';

const RUNTIME_VERSION = 'r8';
const viteEnv = import.meta.env as unknown as { readonly VITE_PARENT_ORIGIN?: string };
const parentOrigin = viteEnv.VITE_PARENT_ORIGIN ?? 'https://build.mintyads.app';
const emptySnapshotFacts: RuntimeSnapshotFacts = {
  layerIds: [],
  mountedLayerIds: [],
  assetUrls: [],
  errorCount: 0,
};

function toRuntimeAnalyticsData(data: PlayableJsonObject | undefined): Record<string, JsonValue> {
  if (!data) return {};
  return { ...data };
}

function toJsonRecord(value: unknown): Record<string, JsonValue> {
  return JSON.parse(JSON.stringify(value)) as Record<string, JsonValue>;
}

function createProbeUnavailableAction(input: {
  readonly id: string;
  readonly kind: RuntimeProbeActionRecord['kind'];
  readonly startedAtMs: number;
  readonly input: Record<string, JsonValue>;
}): RuntimeProbeActionRecord {
  return {
    id: input.id,
    kind: input.kind,
    startedAtMs: input.startedAtMs,
    finishedAtMs: Date.now(),
    input: input.input,
    result: 'error',
    summary: 'runtime probe controls unavailable',
  };
}

export function RuntimeApp() {
  const [state, dispatch] = React.useReducer(runtimeReducer, initialRuntimeState);
  const highlightClearTimerRef = React.useRef<number | null>(null);
  const stateRef = React.useRef(state);
  const snapshotFactsRef = React.useRef<RuntimeSnapshotFacts>(emptySnapshotFacts);
  const match3ControlsRef = React.useRef<RuntimeMatch3Controls | null>(null);
  const probeControlsRef = React.useRef<RuntimeProbeControls | null>(null);
  const probeTraceRef = React.useRef<RuntimeProbeTraceBuffer | null>(null);
  const probeTraceSampleTimerRef = React.useRef<number | null>(null);
  const probeTraceSampleIndexRef = React.useRef(0);

  React.useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const postParent = React.useCallback((message: RuntimeToParentMessage) => {
    window.parent.postMessage(message, parentOrigin);
  }, []);

  const clearHighlightTimer = React.useCallback(() => {
    if (highlightClearTimerRef.current === null) return;
    window.clearTimeout(highlightClearTimerRef.current);
    highlightClearTimerRef.current = null;
  }, []);

  const clearProbeTraceSampleTimer = React.useCallback(() => {
    if (probeTraceSampleTimerRef.current === null) return;
    window.clearInterval(probeTraceSampleTimerRef.current);
    probeTraceSampleTimerRef.current = null;
  }, []);

  const handleLayerSelected = React.useCallback((layerId: string | null) => {
    postParent(createRuntimeMessage('runtime.layerSelected', { layerId }));
  }, [postParent]);

  const handleRuntimeError = React.useCallback((code: string, message: string) => {
    postParent(createRuntimeMessage('runtime.error', { code, message }));
  }, [postParent]);

  const handleRuntimeAnalytics = React.useCallback((name: string, data?: PlayableJsonObject) => {
    postParent(createRuntimeMessage('runtime.analytics', {
      name,
      data: toRuntimeAnalyticsData(data),
    }));
  }, [postParent]);

  const handleRuntimeInput = React.useCallback((payload: RuntimeInputPayload) => {
    postParent(createRuntimeMessage('runtime.input', payload));
  }, [postParent]);

  const handleProjectRendered = React.useCallback((payload: {
    projectId: string;
    layerCount: number;
    assetCount: number;
  }) => {
    postParent(createRuntimeMessage('runtime.projectRendered', payload));
  }, [postParent]);

  const handleRuntimeFacts = React.useCallback((facts: RuntimeSnapshotFacts) => {
    snapshotFactsRef.current = facts;
  }, []);

  const handleRuntimeControls = React.useCallback((controls: RuntimeMatch3Controls | null) => {
    match3ControlsRef.current = controls;
  }, []);

  const handleRuntimeProbeControls = React.useCallback((controls: RuntimeProbeControls | null) => {
    probeControlsRef.current = controls;
  }, []);

  React.useEffect(() => {
    postParent(createRuntimeMessage('runtime.ready', { runtimeVersion: RUNTIME_VERSION }));
  }, [postParent]);

  React.useEffect(() => {
    const onMessage = (event: MessageEvent<unknown>) => {
      if (event.source !== window.parent) return;
      if (event.origin !== parentOrigin) return;

      const parsed = ParentToRuntimeMessageSchema.safeParse(event.data);
      if (!parsed.success) return;

      const message: ParentToRuntimeMessage = parsed.data;
      dispatch(message);

      if (
        message.type === 'runtime.clearHighlights' ||
        message.type === 'runtime.init' ||
        message.type === 'runtime.restart'
      ) {
        clearHighlightTimer();
      }

      if (message.type === 'runtime.init' || message.type === 'runtime.restart') {
        clearProbeTraceSampleTimer();
        probeTraceRef.current = null;
      }

      if (message.type === 'runtime.highlightLayers') {
        clearHighlightTimer();
        highlightClearTimerRef.current = window.setTimeout(() => {
          dispatch(createRuntimeMessage('runtime.clearHighlights', {}));
          highlightClearTimerRef.current = null;
        }, message.payload.durationMs);
      }

      if (message.type === 'runtime.ping') {
        postParent(createRuntimeMessage('runtime.pong', {
          sentAt: message.payload.sentAt,
          receivedAt: Date.now(),
        }, message.requestId));
      }

      if (message.type === 'runtime.queryState') {
        const currentState = stateRef.current;
        const facts = snapshotFactsRef.current;
        postParent(createRuntimeMessage('runtime.stateSnapshot', {
          projectId: currentState.project?.id ?? '',
          runtimeVersion: RUNTIME_VERSION,
          layerIds: [...facts.layerIds],
          mountedLayerIds: [...facts.mountedLayerIds],
          assetUrls: [...facts.assetUrls],
          viewport: currentState.viewport,
          errorCount: facts.errorCount,
        }, message.requestId));
      }

      if (message.type === 'runtime.driveInput') {
        // Drive a KNOWN move as REAL pointer events on the input layer (PixiRuntimeCanvas), never a direct handler call.
        match3ControlsRef.current?.driveInput(message.payload);
      }

      if (message.type === 'runtime.queryMatch3') {
        // Reply with the live window.__match3 logical state + rendered Pixi client-px bounds (fail-closed if not ready).
        const snapshot = match3ControlsRef.current?.getMatch3Snapshot() ?? FAIL_CLOSED_MATCH3_SNAPSHOT;
        postParent(createRuntimeMessage('runtime.match3Snapshot', snapshot, message.requestId));
      }

      if (message.type === 'runtime.probeSnapshot') {
        const snapshot = probeControlsRef.current?.getProbeSnapshot(message.payload.label ?? 'probe');
        if (snapshot) {
          postParent(createRuntimeMessage('runtime.probeSnapshotResult', snapshot, message.requestId));
        } else {
          postParent(createRuntimeMessage('runtime.error', {
            requestId: message.requestId,
            code: 'runtime-probe-unavailable',
            message: 'runtime probe controls unavailable',
          }, message.requestId));
        }
      }

      if (message.type === 'runtime.probeStartTrace') {
        const startedAtMs = Date.now();
        clearProbeTraceSampleTimer();
        probeTraceSampleIndexRef.current = 0;
        probeTraceRef.current = createRuntimeProbeTraceBuffer({
          projectId: stateRef.current.project?.id ?? '',
          scenario: message.payload.scenario,
          startedAtMs,
        });
        const snapshot = probeControlsRef.current?.getProbeSnapshot(`trace-start:${message.payload.scenario}`);
        if (snapshot) probeTraceRef.current.addSnapshot(snapshot);
        if (message.payload.sampleEveryMs !== undefined) {
          probeTraceSampleTimerRef.current = window.setInterval(() => {
            const trace = probeTraceRef.current;
            const controls = probeControlsRef.current;
            if (!trace || !controls) return;
            probeTraceSampleIndexRef.current += 1;
            const sampled = controls.getProbeSnapshot(`trace-sample:${message.payload.scenario}:${String(probeTraceSampleIndexRef.current)}`);
            trace.addSnapshot(sampled);
          }, message.payload.sampleEveryMs);
        }
        postParent(createRuntimeMessage('runtime.probeActionResult', {
          id: message.requestId ?? `probe-start-${String(startedAtMs)}`,
          kind: 'query',
          startedAtMs,
          finishedAtMs: Date.now(),
          input: { scenario: message.payload.scenario },
          result: 'accepted',
          summary: 'probe trace started',
        }, message.requestId));
      }

      if (message.type === 'runtime.probeDriveAction') {
        const startedAtMs = Date.now();
        const before = probeControlsRef.current?.getProbeSnapshot(`before:${message.payload.kind}`);
        if (before) probeTraceRef.current?.addSnapshot(before);
        const action = probeControlsRef.current?.driveProbeAction?.(message.payload)
          ?? createProbeUnavailableAction({
            id: message.requestId ?? `probe-action-${String(startedAtMs)}`,
            kind: message.payload.kind,
            startedAtMs,
            input: toJsonRecord(message.payload),
          });
        probeTraceRef.current?.addAction(action);
        const after = probeControlsRef.current?.getProbeSnapshot(`after:${message.payload.kind}`);
        if (after) probeTraceRef.current?.addSnapshot(after);
        postParent(createRuntimeMessage('runtime.probeActionResult', {
          ...action,
          ...(before ? { before } : {}),
          ...(after ? { after } : {}),
        }, message.requestId));
      }

      if (message.type === 'runtime.probeStopTrace') {
        const finishedAtMs = Date.now();
        clearProbeTraceSampleTimer();
        const trace = probeTraceRef.current?.finish(finishedAtMs) ?? createRuntimeProbeTraceBuffer({
          projectId: stateRef.current.project?.id ?? '',
          scenario: 'none',
          startedAtMs: finishedAtMs,
        }).finish(finishedAtMs);
        probeTraceRef.current = null;
        postParent(createRuntimeMessage('runtime.probeTraceResult', trace, message.requestId));
      }

      if (message.type === 'runtime.applyPatch' && message.requestId) {
        postParent(createRuntimeMessage('runtime.patchApplied', {
          requestId: message.requestId,
          ok: true,
          patchPaths: message.payload.patch.map((op) => op.path),
        }, message.requestId));
      }
    };

    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
      clearProbeTraceSampleTimer();
    };
  }, [clearHighlightTimer, clearProbeTraceSampleTimer, postParent]);

  React.useEffect(() => clearHighlightTimer, [clearHighlightTimer]);

  return (
    <RuntimeCanvas
      key={state.restartNonce}
      project={state.project}
      viewport={state.viewport}
      highlightedLayerIds={state.highlightedLayerIds}
      assetBaseUrl={parentOrigin}
      onLayerSelected={handleLayerSelected}
      onRuntimeError={handleRuntimeError}
      onRuntimeAnalytics={handleRuntimeAnalytics}
      onRuntimeInput={handleRuntimeInput}
      onRuntimeControls={handleRuntimeControls}
      onRuntimeProbeControls={handleRuntimeProbeControls}
      onProjectRendered={handleProjectRendered}
      onRuntimeFacts={handleRuntimeFacts}
    />
  );
}
